'use client'

import { useEffect, useState, type CSSProperties } from 'react'
import Artwork from './Artwork'
import type { PlayerTrack } from '@/lib/player-context'

/**
 * Disque vinyle réaliste : galette (noire ou blanche) avec sillons concentriques,
 * reflet spéculaire et la pochette de l'album collée au centre (étiquette du
 * 45 tours) avec le trou central. Tourne en continu pendant la lecture,
 * s'arrête en pause.
 *
 * variant : 'black' (défaut) ou 'white'. Si non fourni, on lit le choix de
 * l'utilisateur dans localStorage ('blc.vinylColor') et on écoute l'événement
 * 'blc-vinyl-color' → la TV et le lecteur reflètent le même choix.
 */
type Variant = 'black' | 'white'
type Props = {
  track: PlayerTrack | null | undefined
  playing: boolean
  size?: number
  variant?: Variant
  /** Si fourni, le BRAS de lecture devient cliquable : le poser sur le disque
   *  lance la musique, le lever la met en pause (bascule play/pause). */
  onToggle?: () => void
}

function readStored(): Variant {
  if (typeof localStorage === 'undefined') return 'black'
  try { return localStorage.getItem('blc.vinylColor') === 'white' ? 'white' : 'black' } catch { return 'black' }
}

export default function VinylDisc({ track, playing, size = 320, variant, onToggle }: Props) {
  // Si le parent ne pilote pas la couleur, on la lit/écoute nous-mêmes.
  const [selfVar, setSelfVar] = useState<Variant>(variant ?? 'black')
  useEffect(() => {
    if (variant) return
    setSelfVar(readStored())
    const on = () => setSelfVar(readStored())
    window.addEventListener('blc-vinyl-color', on)
    window.addEventListener('storage', on)
    return () => { window.removeEventListener('blc-vinyl-color', on); window.removeEventListener('storage', on) }
  }, [variant])
  const v: Variant = variant ?? selfVar
  const white = v === 'white'

  const labelSize = Math.round(size * 0.42) // étiquette centrale (~42 % du disque)
  const holeSize = Math.max(10, size * 0.035)

  // ---- BRAS DE LECTURE (tonearm) ----
  // Pivot en haut à droite ; le bras pend vers le bas. Angle POSÉ (playing) =
  // aiguille sur les sillons du disque ; angle LEVÉ (pause) = aiguille parquée à
  // droite, hors du disque. Le mouvement du bras raconte l'action de lancer.
  const baseD = Math.round(size * 0.15)             // socle/pivot du bras
  const armW = Math.max(4, Math.round(size * 0.028))
  const armLen = Math.round(size * 0.66)
  const px = size * 0.9                             // centre du pivot (x)
  const py = size * 0.09                            // centre du pivot (y)
  const playAngle = 14                              // posé sur le disque
  const parkAngle = -18                             // levé / parqué

  // Matière du disque selon la couleur.
  const disc: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: white
      ? [
          // reflet diagonal doux
          'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 28%, rgba(0,0,0,0.05) 72%, rgba(255,255,255,0.35) 100%)',
          // sillons fins clairs
          'repeating-radial-gradient(circle at 50% 50%, #dcdce2 0px, #dcdce2 1px, #f6f6f9 2px, #dcdce2 3px)',
          // dégradé vinyle blanc global
          'radial-gradient(circle at 50% 50%, #ffffff 0%, #ececf1 58%, #d9d9e0 100%)',
        ].join(',')
      : [
          // reflet diagonal
          'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.06) 100%)',
          // sillons fins
          'repeating-radial-gradient(circle at 50% 50%, #0b0b0f 0px, #0b0b0f 1px, #17171d 2px, #0b0b0f 3px)',
          // dégradé vinyle noir global
          'radial-gradient(circle at 50% 50%, #1a1a22 0%, #050507 60%, #000 100%)',
        ].join(','),
    boxShadow: white
      ? '0 0 0 1px rgba(0,0,0,0.10) inset, 0 18px 50px rgba(0,0,0,0.35), 0 0 40px rgb(var(--a1)/0.20)'
      : '0 0 0 1px rgba(255,255,255,0.06) inset, 0 18px 50px rgba(0,0,0,0.6), 0 0 40px rgb(var(--a1)/0.25)',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // rotation : 4 s/tour. animation-play-state stoppe la rotation en pause
    animation: 'spinSlow 4s linear infinite',
    animationPlayState: playing ? 'running' : 'paused',
    transition: 'box-shadow .4s ease',
    willChange: 'transform',
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        filter: playing ? 'none' : 'saturate(0.85) brightness(0.9)',
        transition: 'filter .5s ease',
      }}
    >
      <div style={disc}>
        {/* Étiquette centrale = pochette album, en cercle */}
        <div
          style={{
            width: labelSize,
            height: labelSize,
            borderRadius: '50%',
            overflow: 'hidden',
            boxShadow: white
              ? '0 0 0 4px #f0f0f4, 0 0 0 5px rgba(0,0,0,0.12)'
              : '0 0 0 4px #0a0a0d, 0 0 0 5px rgba(255,255,255,0.12)',
            position: 'relative',
          }}
        >
          <Artwork
            track={track}
            size={labelSize}
            rounded="rounded-full"
            className="w-full h-full"
            transition
          />
          {/* trou central du vinyle */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: holeSize,
              height: holeSize,
              transform: 'translate(-50%,-50%)',
              borderRadius: '50%',
              background: white
                ? 'radial-gradient(circle, #d9d9e0 40%, #f6f6f9 100%)'
                : 'radial-gradient(circle, #000 40%, #222 100%)',
              boxShadow: white ? '0 0 0 2px rgba(0,0,0,0.18)' : '0 0 0 2px rgba(255,255,255,0.15)',
            }}
          />
        </div>

        {/* Reflet "lumière" qui balaie le disque */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: white
              ? 'conic-gradient(from 210deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.5) 25deg, rgba(255,255,255,0) 60deg, rgba(255,255,255,0) 360deg)'
              : 'conic-gradient(from 210deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.07) 25deg, rgba(255,255,255,0) 60deg, rgba(255,255,255,0) 360deg)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* ===== BRAS DE LECTURE (tonearm) ===== */}
      {/* Le BRAS (rotatif) : pivote autour du socle. Cliquer = poser/lever → play/pause. */}
      <div
        onClick={onToggle ? (e) => { e.stopPropagation(); onToggle() } : undefined}
        role={onToggle ? 'button' : undefined}
        aria-label={onToggle ? (playing ? 'Lever le bras (pause)' : 'Poser le bras (lecture)') : undefined}
        title={onToggle ? (playing ? 'Lever le bras — pause' : 'Poser le bras sur le disque — lecture') : undefined}
        style={{
          position: 'absolute',
          left: px - armW * 1.75,
          top: py,
          width: armW * 3.5,               // zone de clic élargie (la tige reste fine)
          height: armLen,
          transformOrigin: '50% 0',
          transform: `rotate(${playing ? playAngle : parkAngle}deg)`,
          transition: 'transform .8s cubic-bezier(.34,1.3,.5,1)',
          cursor: onToggle ? 'pointer' : 'default',
          zIndex: 6,
        }}
      >
        {/* tige métallique du bras (fine, centrée dans la zone de clic) */}
        <div style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: armW, height: armLen, borderRadius: armW,
          background: 'linear-gradient(90deg, #8b909c 0%, #eef0f6 45%, #c2c6d0 55%, #7c808b 100%)',
          boxShadow: '0 2px 5px rgba(0,0,0,.45)',
        }} />
        {/* contrepoids (en haut, derrière le pivot) */}
        <div style={{
          position: 'absolute', top: -armW * 1.6, left: '50%', transform: 'translateX(-50%)',
          width: armW * 2.2, height: armW * 2.2, borderRadius: '50%',
          background: 'radial-gradient(circle at 40% 35%, #3a3d46, #16181d 70%)',
          boxShadow: '0 2px 6px rgba(0,0,0,.55)',
        }} />
        {/* tête de lecture + cellule (en bas) */}
        <div style={{
          position: 'absolute', bottom: -armW * 1.1, left: '50%', transform: 'translateX(-50%)',
          width: armW * 2.6, height: armW * 3.2, borderRadius: 3,
          background: 'linear-gradient(180deg, #2b2e37, #14161b)',
          boxShadow: playing
            ? '0 0 10px 1px rgb(var(--a1)/.55), 0 2px 6px rgba(0,0,0,.5)'
            : '0 2px 6px rgba(0,0,0,.5)',
        }}>
          {/* pointe / aiguille */}
          <div style={{
            position: 'absolute', bottom: -armW * 0.8, left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: `${armW * 0.55}px solid transparent`,
            borderRight: `${armW * 0.55}px solid transparent`,
            borderTop: `${armW * 0.9}px solid #d7dae2`,
          }} />
        </div>
      </div>

      {/* socle / pivot du bras (fixe) */}
      <div style={{
        position: 'absolute', left: px - baseD / 2, top: py - baseD / 2,
        width: baseD, height: baseD, borderRadius: '50%',
        background: 'radial-gradient(circle at 38% 32%, #eef0f6, #9aa0ad 58%, #5f636d)',
        boxShadow: '0 4px 12px rgba(0,0,0,.55), inset 0 0 0 2px rgba(255,255,255,.35)',
        zIndex: 5, pointerEvents: 'none',
      }} />

      {/* Halo d'invitation quand en PAUSE (montre que le bras est cliquable). */}
      {onToggle && !playing && (
        <div style={{
          position: 'absolute', left: px - baseD / 2, top: py - baseD / 2,
          width: baseD, height: baseD, borderRadius: '50%',
          boxShadow: '0 0 0 0 rgb(var(--a1)/.5)',
          animation: 'armPulse 1.8s ease-out infinite', zIndex: 4, pointerEvents: 'none',
        }} />
      )}
    </div>
  )
}
